import React, { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { useParams, useSearchParams } from "react-router-dom";
import {
  resetVaultViewState,
  setActiveRequirement,
  setActiveVault,
} from "../../redux/slices/vaultSlice.ts";
import { useVaultFolder } from "../../hooks/vault/queries/useVaultFolder.ts";
import { useDocumentTitle } from "../../hooks/useDocumentTitle.ts";
import { NotFoundError } from "../errors/NotFoundError.tsx";
import { setDemoMode } from "../../utils/vault/vaultActorSession.ts";
import { VaultSkeleton, VaultPageRedesign } from "./ui";
import { folderHasProcessingProofOfAddress } from "./utils/requirementGrouping.ts";
import type { Folder } from "../../redux/interfaces/folder.types.ts";

export const DocumentationVault: React.FC = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { jwtToken, activeRequirement: storeActiveRequirement } =
    useAppSelector((state) => state.vault);
  const getRefetchInterval = useCallback(
    (currentFolder: Folder | undefined) =>
      folderHasProcessingProofOfAddress(currentFolder) ? 2_000 : false,
    [],
  );
  const {
    data: folder,
    isPending,
    isError,
    error,
  } = useVaultFolder(params.id, jwtToken, { getRefetchInterval });

  useEffect(() => {
    dispatch(resetVaultViewState());
  }, [dispatch, params.id]);

  useEffect(() => {
    const folderId = params.id;
    if (!folderId) return;
    const value = searchParams.get("test_mode");
    if (value === "true") setDemoMode(folderId, true);
    else if (value === "false") setDemoMode(folderId, false);
  }, [params.id, searchParams]);

  // Reflect the folder number in the browser tab. Leaving the value as `null`
  // while the folder is still loading keeps whatever index.html had so the
  // tab doesn't flash an intermediate title.
  const documentTitle = folder
    ? `${folder.folder_number} - Multiexpediente`
    : null;
  useDocumentTitle(documentTitle, { defaultOnUnmount: "Multiexpediente" });

  // Keep activeVault in Redux in sync with TQ data (other components still read it)
  useEffect(() => {
    if (folder) {
      dispatch(setActiveVault(folder));
    }
  }, [folder, dispatch]);

  // Sync activeRequirement with fresh folder data
  const activeRequirementRef = useRef(storeActiveRequirement);
  activeRequirementRef.current = storeActiveRequirement;
  useEffect(() => {
    const activeReq = activeRequirementRef.current;
    if (activeReq && folder?.subfolders) {
      for (const subfolder of folder.subfolders) {
        const updated = subfolder.requirements?.find(
          (r) => r.id === activeReq.id,
        );
        if (updated) {
          dispatch(setActiveRequirement(updated));
          break;
        }
      }
    }
  }, [folder, dispatch]);

  if (isError)
    return (
      <NotFoundError
        description={error?.message ?? "Vault not found"}
        returnToMainPage={false}
      />
    );

  if (folder && !folder.is_active)
    return (
      <NotFoundError
        description="La solicitud de renta solicitada no está disponible en este momento"
        returnToMainPage={false}
      />
    );

  if (isPending || !folder) {
    return <VaultSkeleton />;
  }

  return <VaultPageRedesign key={folder.id} folder={folder} />;
};
